import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { sanitizeError } from '@/lib/utils/errors';

import { useAuthStatus } from '../../allauth_auth';
import FormErrors from '../../components/FormErrors';
import AuthLayout from '../../layouts/AuthLayout';
import { confirmLoginCode, Flows } from '../../lib/allauth';
import type { AllauthResponse } from '../../types/allauth';

export default function ConfirmLoginCode() {
  const [, authInfo] = useAuthStatus();
  const [code, setCode] = useState('');
  const [response, setResponse] = useState<{
    fetching: boolean;
    content: AllauthResponse | null;
  }>({ fetching: false, content: null });

  function submit() {
    setResponse({ ...response, fetching: true });
    confirmLoginCode(code)
      .then(content => {
        setResponse(r => {
          return { ...r, content };
        });
      })
      .catch(e => {
        console.error(e);
        toast.error(sanitizeError(e));
      })
      .then(() => {
        setResponse(r => {
          return { ...r, fetching: false };
        });
      });
  }

  if (
    response.content?.status === 409 ||
    authInfo.pendingFlow?.id !== Flows.LOGIN_BY_CODE
  ) {
    return <Navigate to='/account/login/code' />;
  }
  return (
    <AuthLayout title='Enter sign-in code'>
      <div className='flex flex-col gap-8'>
        {/* Header */}
        <div className='flex flex-col gap-3'>
          <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
            Enter sign-in code
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-400'>
            We've sent a 6-digit code to your email. Enter it below to sign in.
          </p>
        </div>

        {/* Form */}
        <div className='flex flex-col gap-6'>
          <FormErrors errors={response.content?.errors} />

          <div>
            <label
              className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5'
              htmlFor='code'
            >
              Verification code
            </label>
            <Input
              id='code'
              type='text'
              className='w-full text-center text-lg font-mono tracking-widest'
              placeholder='000000'
              isRequired
              onChange={value => setCode(value)}
              value={code}
              name='code'
              maxLength={6}
              autoComplete='one-time-code'
            />
            <FormErrors param='code' errors={response.content?.errors} />
            <p className='mt-1.5 text-xs text-gray-500 dark:text-gray-400 text-center'>
              Code expires in a few minutes
            </p>
          </div>

          <Button
            onClick={() => submit()}
            disabled={response.fetching || code.length < 6}
            className='w-full'
            isLoading={response.fetching}
            showTextWhileLoading
          >
            {response.fetching ? 'Verifying…' : 'Sign in'}
          </Button>

          <div className='text-center flex flex-col gap-2'>
            <Link
              to='/account/login/code'
              className='text-sm text-fg-brand-primary hover:text-utility-brand-700 font-medium'
            >
              Resend code
            </Link>
            <Link
              to='/account/login'
              className='text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            >
              Back to password sign in
            </Link>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
